import React, { useEffect, useState } from "react";
import RfqBannerStats from "@/components/admin/RfqBannerStats";
import { ArrowDown, ArrowUp, RefreshCw, Search, MailQuestion, Download, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";
import CampaignToolsPanel from "@/components/admin/CampaignToolsPanel";

const fmt = (iso) => {
  const d = new Date(iso || "");
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const SOURCE_META = {
  listing_enquiry: { label: "Listing enquiry", cls: "bg-[#E8ECE9] text-[#4A5550]" },
  site: { label: "Site RFQ", cls: "bg-[#EEF3F0] text-[#264839]" },
  campaign: { label: "Campaign (/get-quotes)", cls: "bg-[#FFF4D6] text-[#8A6400]" },
};

export default function EnquiriesManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("desc");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  const [replied, setReplied] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [source, setSource] = useState("");
  const [sourceCounts, setSourceCounts] = useState({});
  const [sourceStats, setSourceStats] = useState({});

  const toggleReplied = async (e) => {
    try {
      const { data } = await api.post(`/admin/enquiries/${e.id}/replied`);
      setItems((rows) => rows.map((r) => (r.id === e.id ? { ...r, replied_at: data.replied_at, response_hours: data.response_hours ?? null } : r)));
      toast.success(data.replied_at ? "Marked as replied" : "Reply mark removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update"); }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ sort });
      if (q.trim()) params.set("q", q.trim());
      if (replied) params.set("replied", replied);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      if (source) params.set("source", source);
      const { data } = await api.get(`/admin/enquiries?${params}`);
      setItems(data.items || []); setTotal(data.total || 0); setSourceCounts(data.source_counts || {}); setSourceStats(data.source_stats || {});
    } catch (e) { setItems([]); setError(describeLoadError(e, "enquiries")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sort, replied, fromDate, toDate, source]); // eslint-disable-line react-hooks/exhaustive-deps

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/enquiries/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="admin-enquiries">
      <RfqBannerStats />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-[#11211A] flex items-center gap-2"><MailQuestion className="w-5 h-5 text-[#264839]" /> Enquiries</h2>
          <p className="text-xs text-[#7D8C85]">Every hotelier enquiry and RFQ, tagged by source — free-listing enquiry, site RFQ, or the /get-quotes campaign page. Copies also land in rfq@accommodationsuppliers.com.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9AA6A0]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hotelier, supplier, message…" className="pl-8 pr-3 py-2 text-sm border border-[#C4CFC9] rounded-sm w-72" data-testid="enquiries-search" />
          </div>
          <button type="button" onClick={load} className="btn-ghost text-xs" data-testid="enquiries-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
          <button type="button" onClick={exportCsv} disabled={exporting} className="btn-ghost text-xs" title="Download all enquiries as CSV" data-testid="enquiries-export-csv"><Download className="w-3.5 h-3.5" /> {exporting ? "Preparing…" : "Download CSV"}</button>
        </form>
        <div className="text-xs text-[#7D8C85]" data-testid="enquiries-count">{error ? "—" : `${total} enquir${total === 1 ? "y" : "ies"}`}</div>
      </div>
      <LoadErrorBanner error={error} onRetry={load} testId="enquiries-error" />
      <CampaignToolsPanel onChanged={load} />
      <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="enquiries-source-stats">
        {Object.entries(SOURCE_META).map(([k, m]) => {
          const st = sourceStats[k] || {};
          return (
            <button type="button" key={k} onClick={() => setSource(source === k ? "" : k)} className={`text-left bg-white border p-3 transition ${source === k ? "border-[#264839]" : "border-[#E8ECE9] hover:border-[#C4CFC9]"}`} data-testid={`source-stat-${k}`}>
              <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${m.cls}`}>{m.label}</span>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-serif text-2xl text-[#11211A]" data-testid={`source-stat-${k}-total`}>{st.total ?? 0}</span>
                <span className="text-[11px] text-[#7D8C85]">received</span>
              </div>
              <div className="text-[11px] text-[#4A5550] mt-1">
                Reply rate <strong data-testid={`source-stat-${k}-rate`}>{st.reply_rate_pct === null || st.reply_rate_pct === undefined ? "—" : `${st.reply_rate_pct}%`}</strong> ({st.replied ?? 0}/{st.total ?? 0}) · Avg response <strong data-testid={`source-stat-${k}-hours`}>{st.avg_response_hours === null || st.avg_response_hours === undefined ? "—" : `${st.avg_response_hours}h`}</strong>
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs" data-testid="enquiries-filters">
        <label className="flex items-center gap-1.5 text-[#7D8C85]">Source
          <select value={source} onChange={(e) => setSource(e.target.value)} className="border border-[#C4CFC9] px-2 py-1.5 rounded-sm text-[#11211A]" data-testid="enquiries-filter-source">
            <option value="">All sources</option>
            {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}{sourceCounts[k] !== undefined ? ` (${sourceCounts[k]})` : ""}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[#7D8C85]">Status
          <select value={replied} onChange={(e) => setReplied(e.target.value)} className="border border-[#C4CFC9] px-2 py-1.5 rounded-sm text-[#11211A]" data-testid="enquiries-filter-replied">
            <option value="">All</option>
            <option value="no">Not replied</option>
            <option value="yes">Replied</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[#7D8C85]">From
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border border-[#C4CFC9] px-2 py-1 rounded-sm text-[#11211A]" data-testid="enquiries-filter-from" />
        </label>
        <label className="flex items-center gap-1.5 text-[#7D8C85]">To
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border border-[#C4CFC9] px-2 py-1 rounded-sm text-[#11211A]" data-testid="enquiries-filter-to" />
        </label>
        {(replied || fromDate || toDate || source) && <button type="button" onClick={() => { setReplied(""); setFromDate(""); setToDate(""); setSource(""); }} className="text-[#264839] underline underline-offset-2" data-testid="enquiries-filter-clear">Clear filters</button>}
      </div>
      <div className="bg-white border border-[#E8ECE9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-[#4A5550]">
            <tr className="text-left">
              <th className="px-3 py-3 font-medium">
                <button type="button" onClick={() => setSort(sort === "desc" ? "asc" : "desc")} className="inline-flex items-center gap-1 hover:text-[#264839]" title="Sort by date" data-testid="enquiries-sort-date">
                  Date {sort === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                </button>
              </th>
              <th className="px-3 py-3 font-medium">Source</th>
              <th className="px-3 py-3 font-medium">Hotelier</th>
              <th className="px-3 py-3 font-medium">Hotelier email</th>
              <th className="px-3 py-3 font-medium">Supplier</th>
              <th className="px-3 py-3 font-medium">Message</th>
              <th className="px-3 py-3 font-medium">Replied</th>
              <th className="px-3 py-3 font-medium">Delivered</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-[#7D8C85]">Loading…</td></tr>}
            {!loading && !error && items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-[#7D8C85]" data-testid="enquiries-empty">No enquiries yet.</td></tr>}
            {!loading && items.map((e) => {
              const open = openId === e.id;
              const src = SOURCE_META[e.source] || SOURCE_META.site;
              return (
                <tr key={e.id} className="border-t border-[#E8ECE9] align-top hover:bg-[#FAFAF8]" data-testid={`enquiry-row-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-[#4A5550] text-xs" data-testid={`enquiry-date-${e.id}`}>{fmt(e.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap"><span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${src.cls}`} data-testid={`enquiry-source-${e.id}`}>{src.label}</span></td>
                  <td className="px-3 py-2"><div className="text-[#11211A]">{e.name}</div>{e.company && <div className="text-[11px] text-[#7D8C85]">{e.company}</div>}</td>
                  <td className="px-3 py-2"><a href={`mailto:${e.email}`} className="text-[#264839] hover:underline" data-testid={`enquiry-email-${e.id}`}>{e.email}</a></td>
                  <td className="px-3 py-2">
                    <div className="text-[#11211A]">{e.supplier_name}</div>
                    {e.listing_id && <a href={`/listing/${e.listing_id}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#264839] hover:underline">View listing ↗</a>}
                  </td>
                  <td className="px-3 py-2 max-w-md">
                    <div className={`text-[#4A5550] text-xs whitespace-pre-wrap ${open ? "" : "line-clamp-2"}`} data-testid={`enquiry-message-${e.id}`}>{e.message}</div>
                    {e.message?.length > 140 && <button type="button" onClick={() => setOpenId(open ? null : e.id)} className="text-[11px] text-[#264839] underline underline-offset-2 mt-1" data-testid={`enquiry-toggle-${e.id}`}>{open ? "Show less" : "Show full message"}</button>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`enquiry-replied-${e.id}`}>
                    {e.replied_at
                      ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#E8ECE9] text-[#264839] px-1.5 py-0.5" title={`Marked replied ${fmt(e.replied_at)}`}><CheckCircle2 className="w-3 h-3" /> Replied{typeof e.response_hours === "number" ? ` · ${e.response_hours}h` : ""}</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#FBECE8] text-[#B35C4A] px-1.5 py-0.5"><Circle className="w-3 h-3" /> Not replied</span>}
                    <button type="button" onClick={() => toggleReplied(e)} className="block mt-1 text-[10px] text-[#264839] underline underline-offset-2" data-testid={`enquiry-toggle-replied-${e.id}`}>{e.replied_at ? "Undo" : "Mark replied"}</button>
                  </td>
                  <td className="px-3 py-2 text-[10px] uppercase tracking-wider whitespace-nowrap">
                    {(e.kind === "rfq" ? ["supplier"] : ["inbox", "supplier", "hotelier"]).map((k) => (
                      <span key={k} className={`inline-block mr-1 px-1.5 py-0.5 ${e.sent?.[k] ? "bg-[#E8ECE9] text-[#264839]" : "bg-[#FBECE8] text-[#B35C4A]"}`} title={`${k} email ${e.sent?.[k] ? "sent" : "not sent"}`}>{k}</span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
